/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Switch, NavLink } from "react-router-dom";
import PrivateRouter from "../common/routes/PrivateRouter";
import HeaderContainer from "../features/header/containers/HeaderContainer";
import FooterContainer from "../features/footer/containers/FooterContainer";
import HomeContainer from "../features/home/containers/HomeContainer";
import LoginContainer from "../features/login/containers/LoginContainer";
import ForgotPassword from "../features/forgotPassword/containers/ForgotPasswordContainer";
import ResetPassword from "../features/resetPassword/containers/ResetPasswordContainer";
import CustomersContainer from "../features/customers/containers/CustomersContainer";
import SeasonsContainer from "../features/seasons/containers/SeasonsContainer";
import ChangePassword from "../features/changePassword/containers/ChangePasswordContainer";
import ErrorContainer from "../features/404/containers/ErrorContainer";
import BrandContainer from "../features/productBrands/containers/ProductBrandContainer";
import PageNotFound from "../common/components/PageNotFound";
import "../common/css/styles.css";
import PublicRouter from "../common/routes/PublicRouter";
import UserData from "../common/security/UserData";
import Permission from "../common/security/Permission";
import Rules from "../common/security/PermissionRules";

const MainRoutes = (props) => {
  const {
    match: { params },
  } = props;

  const baseRoute = (params && params.baseRoute) || null;

  const [navigation, setNavigation] = useState(null);

  const [showActive, setShowActive] = useState(true);

  const toggle = () => setShowActive(!showActive);

  useEffect(() => {
    setNavigation(baseRoute);
  }, [baseRoute]);

  const handleLogout = async () => {
    UserData.clearAll();
  };

  return (
    <>
      <HeaderContainer navigation={navigation} handleLogout={handleLogout} />
      <div id="mainWrapper" className="body-outer">
        <div className={`navbar-outer ${showActive ? "" : "active"}`}>
          <ul className="nav-list">
            <li className="nav-list-item">
              <a href="##" className="nav-link" onClick={toggle}>
                <div className="nav-icon">
                  <i className="fal fa-bars i-menu-closed"> </i>
                  <i className="fal fa-arrow-left i-menu-open"> </i>
                </div>
                <div className="nav-text">Main Menu</div>
              </a>
            </li>
            <Permission
              allowed={Rules.Control.VIEW_CUSTOMERS_LINK}
            >
              <li className="nav-list-item active">
                <NavLink exact to="/customers" className="nav-link" activeClassName="active">
                  <div className="nav-icon">
                    <i className="fal fa-user-friends"> </i>
                  </div>
                  <div className="nav-text">B2B Customers</div>
                </NavLink>
              </li>
            </Permission>
            <Permission
              allowed={Rules.Control.VIEW_SEASONS_LINK}
            >
              <li className="nav-list-item">
                <NavLink exact to="/seasons" className="nav-link" activeClassName="active">
                  <div className="nav-icon">
                    <i className="fal fa-clock"> </i>
                  </div>
                  <div className="nav-text">Seasons</div>
                </NavLink>
              </li>
            </Permission>
            <Permission
              allowed={Rules.Control.VIEW_BRANDS_LINK}
            >
              <li className="nav-list-item">
                <NavLink exact to="/product-brands" className="nav-link" activeClassName="active">
                  <div className="nav-icon">
                    <i className="fal fa-tags"> </i>
                  </div>
                  <div className="nav-text">Brands</div>
                </NavLink>
              </li>
            </Permission>
          </ul>
          <FooterContainer />
        </div>
        <div className="content-outer">
          <Switch>
            <PrivateRouter exact path="/home" component={HomeContainer} />
            <PublicRouter exact path="/login" component={LoginContainer} />
            <PublicRouter
              exact
              path="/forgot-password"
              component={ForgotPassword}
            />
            <PublicRouter
              exact
              path="/reset-password"
              component={ResetPassword}
            />
            <PrivateRouter
              exact
              path="/customers"
              component={CustomersContainer}
            />
            <PrivateRouter
              exact
              path="/seasons"
              component={SeasonsContainer}
            />
            <PrivateRouter
              exact
              path="/change-password"
              component={ChangePassword}
            />
            <PublicRouter exact path="/product-brands" component={BrandContainer} />
            <PublicRouter exact path="/not-found" component={PageNotFound} />
            <PublicRouter component={ErrorContainer} />
          </Switch>
        </div>
      </div>
    </>
  );
};

MainRoutes.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string,
    params: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
};

export default MainRoutes;
